import { useState } from "react";
import type { DecaDocument } from "@deca/shared-types";
import { useUpdateIncident } from "../api/hooks";
import { Button, Card, ErrorNotice, Field, Textarea } from "./ui";
import { formatDateTime } from "../lib/format";

/**
 * Incidencia manual del documento (CLAUDE.md Fase 3.15 y 4).
 *
 * Es una señal **manual y separada** de la corrección automática (que se
 * deduce del número de versiones). No se fusionan en un único
 * "correcto/incorrecto": son dos preguntas distintas y el reporte las informa
 * por separado.
 */
export function IncidentPanel({ document }: { document: DecaDocument }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(document.incidentNotes ?? "");
  const mutation = useUpdateIncident();

  const save = (hasIncidents: boolean): void => {
    mutation.mutate({
      id: document.id,
      hasIncidents,
      incidentNotes: hasIncidents ? notes.trim() || null : null,
    });
    setOpen(false);
  };

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="micro-label">Incidencia</h2>
          <p className="mt-1 text-sm text-ink-600 dark:text-ink-300">
            {document.hasIncidents ? "Marcada como incidencia" : "Sin incidencia registrada"}
          </p>
          {document.incidentReviewedAt ? (
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
              Revisada el {formatDateTime(document.incidentReviewedAt)}
            </p>
          ) : null}
        </div>
        <Button onClick={() => setOpen((value) => !value)}>
          {open ? "Cancelar" : document.hasIncidents ? "Editar" : "Marcar incidencia"}
        </Button>
      </div>

      {mutation.isError ? (
        <div className="mt-3">
          <ErrorNotice error={mutation.error} />
        </div>
      ) : null}

      {open ? (
        <div className="mt-4 space-y-3">
          <Field
            label="Notas de la incidencia"
            htmlFor="incident-notes"
            hint="Queda registrado quién lo marca y cuándo; el apunte no se puede borrar del registro de auditoría."
          >
            <Textarea
              id="incident-notes"
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Ej.: el destinatario rechazó la mercancía por embalaje dañado."
            />
          </Field>
          <div className="flex gap-2">
            <Button variant="primary" disabled={mutation.isPending} onClick={() => save(true)}>
              Guardar incidencia
            </Button>
            {document.hasIncidents ? (
              <Button disabled={mutation.isPending} onClick={() => save(false)}>
                Quitar la marca
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </Card>
  );
}
